import { get, set } from "idb-keyval";
import { api, uid } from "./api";

const KEY = "cd-pending-captures";
const listeners = new Set();
let syncing = false;

export const onQueueChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const notify = async () => { const q = await getQueue(); listeners.forEach((fn) => fn(q, syncing)); };

export async function getQueue() {
  try { return (await get(KEY)) || []; } catch { return []; }
}

async function saveQueue(q) { await set(KEY, q); await notify(); }

async function sendItem(item) {
  if (item.kind === "upload") {
    const fd = new FormData();
    item.files.forEach((f, i) => fd.append("files", f, item.names?.[i] || `card-${i}.jpg`));
    fd.append("client_id", item.client_id);
    fd.append("source_type", item.source_type || "card_photo");
    if (item.batch_id) fd.append("batch_id", item.batch_id);
    return (await api.post("/captures/upload", fd)).data;
  }
  if (item.kind === "text") return (await api.post("/captures/text", { text: item.text, client_id: item.client_id })).data;
  if (item.kind === "qr") return (await api.post("/captures/qr", { content: item.text, client_id: item.client_id })).data;
  if (item.kind === "manual") return (await api.post("/contacts/manual", { ...item.data, client_id: item.client_id })).data;
}

const isNetworkError = (e) => !e.response;

// Try to send now; if offline or the network fails, keep it on the device. client_id makes retries idempotent server-side.
export async function submitCapture(item) {
  const full = { ...item, client_id: item.client_id || uid(), created_at: new Date().toISOString() };
  if (navigator.onLine) {
    try {
      return { synced: true, result: await sendItem(full) };
    } catch (e) {
      if (!isNetworkError(e)) throw e;
    }
  }
  const q = await getQueue();
  q.push(full);
  await saveQueue(q);
  return { synced: false, client_id: full.client_id };
}

export async function syncQueue() {
  if (syncing || !navigator.onLine) return;
  syncing = true;
  await notify();
  try {
    let q = await getQueue();
    for (const item of [...q]) {
      try {
        await sendItem(item);
        q = (await getQueue()).filter((x) => x.client_id !== item.client_id);
        await set(KEY, q);
      } catch (e) {
        if (isNetworkError(e)) break;
        q = (await getQueue()).map((x) => (x.client_id === item.client_id ? { ...x, error: e.response?.data?.detail || "Upload rejected" } : x));
        await set(KEY, q);
      }
    }
  } finally {
    syncing = false;
    await notify();
  }
}

export async function discardItem(client_id) {
  await saveQueue((await getQueue()).filter((x) => x.client_id !== client_id));
}

if (typeof window !== "undefined") window.addEventListener("online", () => syncQueue());
